import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../auth/AuthProvider";
import { useGeneratorDetail, useClaimGenerator } from "../../features/generators/hooks/useGenerators";
import { STATUS_LABELS, isPlannedStatus } from "../../api/generators";
import { fetchGeneratorImpact, type GeneratorImpact } from "../../api/pipeline";
import { getBaselineSummary } from "../../api/baseline";

const SCREENING_CF: Record<string, number> = {
  "Solar Photovoltaic": 0.22,
  "Solar Thermal with Energy Storage": 0.22,
  "Solar Thermal without Energy Storage": 0.22,
  "Onshore Wind Turbine": 0.35,
  "Offshore Wind Turbine": 0.45,
  "Conventional Hydroelectric": 0.40,
  "Nuclear": 0.90,
  "Geothermal": 0.90,
};

function getStatusBadgeColor(status: string | null): string {
  if (!status) return "bg-gray-500/20 text-gray-400";
  if (status === "OP") return "bg-green-500/20 text-green-400";
  if (status === "SB") return "bg-yellow-500/20 text-yellow-400";
  if (status === "P" || status === "L") return "bg-blue-500/20 text-blue-400";
  if (["T", "U", "V"].includes(status)) return "bg-orange-500/20 text-orange-400";
  if (status === "TS") return "bg-red-500/20 text-red-400";
  return "bg-gray-500/20 text-gray-400";
}

const GeneratorDetail: React.FC = () => {
  const { plantId, generatorId } = useParams<{ plantId: string; generatorId: string }>();
  const { token } = useAuth();
  const nav = useNavigate();
  const { data: gen, isLoading, error } = useGeneratorDetail(plantId ?? null, generatorId ?? null);
  const claim = useClaimGenerator();

  // Fetch pipeline impact data if available (non-blocking, silent fail)
  const { data: impact } = useQuery<GeneratorImpact>({
    queryKey: ["generatorImpact", plantId, generatorId],
    queryFn: () => fetchGeneratorImpact(plantId!, generatorId!),
    enabled: !!plantId && !!generatorId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch baseline rates for screening estimate
  const { data: baseline } = useQuery({
    queryKey: ["gridBaseline", gen?.ba_code],
    queryFn: () => getBaselineSummary(gen!.ba_code!),
    enabled: !!gen?.ba_code,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const handleClaim = async () => {
    if (!plantId || !generatorId) return;

    if (!token) {
      nav(`/login`);
      return;
    }

    try {
      const result = await claim.mutateAsync({ plantId, generatorId, token });
      nav(`/app/projects/${result.project_id}`);
    } catch (err) {
      // Error handled by mutation state
    }
  };

  if (!plantId || !generatorId) return null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-quantum-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !gen) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-health-400">Generator not found.</p>
        <Link to="/generators" className="text-quantum-400 mt-2 inline-block">
          &larr; Back to search
        </Link>
      </div>
    );
  }

  const hasCoords = gen.latitude != null && gen.longitude != null;
  const planned = isPlannedStatus(gen.status);
  const statusLabel = gen.status ? (STATUS_LABELS[gen.status] || gen.status) : "Unknown";

  // Differentiate claim button text based on generator status
  const claimButtonText = planned
    ? "Run Full Analysis"
    : "Claim as Operating Project";
  const claimDescription = planned
    ? "Run a full lifecycle impact analysis for this planned generator using SAM-modeled generation profiles."
    : "Claim this generator to get 6 months of grid impact data and upgrade for full LCA results.";

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <Link
            to="/generators"
            className="text-caption text-text-muted hover:text-text-primary transition-colors mb-2 inline-block"
          >
            &larr; Back to generators
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-h2 font-bold text-text-primary">
              {gen.plant_name || `${gen.plant_id}-${gen.generator_id}`}
            </h1>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(gen.status)}`}>
              {statusLabel}
            </span>
          </div>
          <p className="text-body-sm text-text-muted mt-1">
            {gen.entity_name} &bull; Generator {gen.generator_id}
          </p>
        </div>

        <button
          onClick={handleClaim}
          disabled={claim.isPending}
          className="btn-primary px-6 py-2 text-body-sm"
        >
          {claim.isPending ? "Claiming..." : claimButtonText}
        </button>
      </div>

      {claim.isError && (
        <div className="mb-4 p-3 rounded-lg bg-health-500/10 border border-health-500/30 text-body-sm text-health-400">
          {claim.error.message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Info cards */}
        <div className="space-y-4">
          <div className="bg-surface-800 rounded-xl p-5 border border-border">
            <h3 className="text-body font-semibold text-text-primary mb-3">
              Generator Details
            </h3>
            <dl className="space-y-2 text-body-sm">
              <Row label="Technology" value={gen.technology} />
              <Row label="Energy Source" value={gen.energy_source_desc} />
              <Row label="Prime Mover" value={gen.prime_mover_code} />
              <Row label="Nameplate Capacity" value={gen.nameplate_capacity_mw ? `${gen.nameplate_capacity_mw.toFixed(1)} MW` : null} />
              <Row label="Net Summer Cap." value={gen.net_summer_capacity_mw ? `${gen.net_summer_capacity_mw.toFixed(1)} MW` : null} />
              <Row label="Status" value={statusLabel} />
              {planned && gen.operating_year_month && (
                <Row label="Expected Online Date" value={gen.operating_year_month} />
              )}
              {!planned && (
                <Row label="Operating Since" value={gen.operating_year_month} />
              )}
              <Row label="Sector" value={gen.sector_name} />
              {gen.planned_retirement_year_month && (
                <Row label="Planned Retirement" value={gen.planned_retirement_year_month} />
              )}
            </dl>
          </div>

          <div className="bg-surface-800 rounded-xl p-5 border border-border">
            <h3 className="text-body font-semibold text-text-primary mb-3">
              Location
            </h3>
            <dl className="space-y-2 text-body-sm">
              <Row label="State" value={gen.state_name ? `${gen.state_name} (${gen.state_id})` : gen.state_id} />
              <Row label="County" value={gen.county} />
              <Row label="Coordinates" value={hasCoords ? `${gen.latitude!.toFixed(4)}, ${gen.longitude!.toFixed(4)}` : null} />
            </dl>
          </div>

          {/* Grid context */}
          <div className="bg-surface-800 rounded-xl p-5 border border-quantum-500/20">
            <h3 className="text-body font-semibold text-quantum-400 mb-3">
              Grid Context
            </h3>
            <dl className="space-y-2 text-body-sm">
              <Row label="Balancing Authority" value={gen.ba_code ? `${gen.ba_name || gen.ba_code} (${gen.ba_code})` : null} />
              {gen.current_carbon_intensity != null && (
                <Row
                  label="Current Grid Carbon Intensity"
                  value={`${gen.current_carbon_intensity.toFixed(1)} kg CO\u2082-eq/MWh`}
                />
              )}
            </dl>
            {gen.ba_code && (
              <Link
                to={`/grid/${gen.ba_code}`}
                className="text-quantum-400 hover:text-quantum-300 text-body-sm mt-3 inline-block transition-colors"
              >
                View grid impact for {gen.ba_code} &rarr;
              </Link>
            )}
          </div>
        </div>

        {/* Map */}
        <div>
          {hasCoords && (
            <div className="h-80 rounded-xl overflow-hidden border border-border">
              <MapContainer
                center={[gen.latitude!, gen.longitude!]}
                zoom={10}
                className="h-full w-full"
                style={{ background: "#0A1628" }}
                zoomControl={false}
              >
                <TileLayer
                  attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                />
                <CircleMarker
                  center={[gen.latitude!, gen.longitude!]}
                  radius={8}
                  pathOptions={{
                    fillColor: "#00b4d8",
                    fillOpacity: planned ? 0.4 : 0.8,
                    weight: 2,
                    color: "#e2e8f0",
                    dashArray: planned ? "4 4" : undefined,
                  }}
                />
              </MapContainer>
            </div>
          )}

          {/* Pipeline impact card (if available) */}
          {impact && impact.dalys_avoided != null && (
            <div className="mt-4 bg-surface-800 rounded-xl p-5 border border-health-500/20">
              <h3 className="text-body font-semibold text-text-primary mb-3">
                Estimated Lifecycle Impact
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-caption text-text-muted">DALYs Avoided</p>
                  <p className="text-h3 font-bold text-health-400">
                    {Math.abs(impact.dalys_avoided!).toFixed(3)}
                  </p>
                </div>
                <div>
                  <p className="text-caption text-text-muted">Ecosystem</p>
                  <p className="text-h3 font-bold text-ecosystem-400">
                    {impact.pdfs_avoided != null ? Math.abs(impact.pdfs_avoided).toFixed(6) : "—"}
                  </p>
                  <p className="text-[10px] text-text-muted">species.yr</p>
                </div>
                <div>
                  <p className="text-caption text-text-muted">Economic Value</p>
                  <p className="text-h3 font-bold text-green-400">
                    ${impact.usd_avoided != null ? Math.abs(impact.usd_avoided).toLocaleString(undefined, { maximumFractionDigits: 0 }) : "—"}
                  </p>
                </div>
              </div>
              <p className="text-[10px] text-text-muted mt-3">
                Full lifecycle analysis estimate. Claim this generator for your detailed report.
              </p>
            </div>
          )}

          {/* Screening impact teaser (fallback when pipeline data unavailable) */}
          {(!impact || impact.dalys_avoided == null) && baseline && gen.nameplate_capacity_mw != null && gen.nameplate_capacity_mw > 0 && (
            (() => {
              const cf = SCREENING_CF[gen.technology ?? ""] ?? 0.25;
              const annualGenMwh = gen.nameplate_capacity_mw! * cf * 8760;
              const dalys = annualGenMwh * (baseline.human_health_dalys_per_mwh ?? 0);
              const speciesYr = annualGenMwh * (baseline.ecosystem_quality_species_yr_per_mwh ?? 0);
              const usd = annualGenMwh * (baseline.resource_scarcity_usd_per_mwh ?? 0);
              return (
                <div className="mt-4 bg-surface-800 rounded-xl p-5 border border-quantum-500/20">
                  <h3 className="text-body font-semibold text-text-primary mb-1">
                    Estimated Annual Grid Impact
                  </h3>
                  <p className="text-caption text-text-muted mb-3">
                    Screening estimate based on {gen.ba_code} grid baseline
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-caption text-text-muted">Health</p>
                      <p className="text-h4 font-bold text-health-400">
                        {dalys < 0.001 && dalys > 0 ? dalys.toExponential(2) : dalys.toFixed(3)}
                      </p>
                      <p className="text-[10px] text-text-muted">DALYs</p>
                    </div>
                    <div>
                      <p className="text-caption text-text-muted">Ecosystem</p>
                      <p className="text-h4 font-bold text-ecosystem-400">
                        {speciesYr < 0.001 && speciesYr > 0 ? speciesYr.toExponential(2) : speciesYr.toFixed(4)}
                      </p>
                      <p className="text-[10px] text-text-muted">species.yr</p>
                    </div>
                    <div>
                      <p className="text-caption text-text-muted">Economic</p>
                      <p className="text-h4 font-bold text-green-400">
                        ${usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </p>
                      <p className="text-[10px] text-text-muted">resource cost</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-text-muted mt-3">
                    Based on {(cf * 100).toFixed(0)}% capacity factor and {gen.ba_code} 30-day baseline rates. Sign in for a full ISO 14044 lifecycle analysis.
                  </p>
                </div>
              );
            })()
          )}

          {/* Sign-in prompt (unauthenticated users) */}
          {!token && (
            <div className="mt-3 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-quantum-500/5 border border-quantum-500/20">
              <svg className="w-4 h-4 text-quantum-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p className="text-body-sm text-text-secondary">
                <a href="/login" className="text-quantum-400 font-medium hover:text-quantum-300">
                  Sign in
                </a>{" "}
                to unlock full lifecycle analysis with SAM-modeled generation profiles.
              </p>
            </div>
          )}

          {/* CTA card */}
          <div className="mt-4 bg-quantum-500/10 rounded-xl p-5 border border-quantum-500/20 text-center">
            <h3 className="text-body font-semibold text-text-primary mb-2">
              {planned ? "Analyze this planned generator" : "Get full lifecycle analysis"}
            </h3>
            <p className="text-body-sm text-text-secondary mb-4">
              {claimDescription}
            </p>
            <button
              onClick={handleClaim}
              disabled={claim.isPending}
              className="btn-primary px-8 py-2"
            >
              {token
                ? claimButtonText
                : "Sign In for Full Report"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between">
      <dt className="text-text-muted">{label}</dt>
      <dd className="text-text-primary font-medium">{value || "N/A"}</dd>
    </div>
  );
}

export default GeneratorDetail;
